"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { toast } from "sonner";
import { LoginForm } from "@/components/auth/login-form";
import { MagicLinkForm } from "@/components/auth/magic-link-form";
import { OAuthButtons } from "@/components/auth/oauth-buttons";
import { FieldSeparator } from "@/components/ui/field";
import { useAuth } from "@/hooks/use-auth";

function SearchParamsHandler({
  onRedirectChange,
}: {
  onRedirectChange: (redirect: string | null) => void;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const redirect = searchParams.get("redirect");
    onRedirectChange(redirect);
  }, [searchParams, onRedirectChange]);

  useEffect(() => {
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");
    const message = searchParams.get("message");

    if (error || message) {
      const errorMessage =
        errorDescription ||
        message ||
        error ||
        "An error occurred during authentication";
      toast.error(errorMessage);
      // Clean up URL but preserve redirect param
      const redirect = searchParams.get("redirect");
      router.replace(
        redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : "/login",
      );
    }
  }, [searchParams, router]);

  return null;
}

export default function LoginPage() {
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && user) {
      router.push(redirectUrl || "/");
    }
  }, [user, loading, router, redirectUrl]);

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-600" />
      </div>
    );
  }

  if (user) {
    return null;
  }

  return (
    <>
      <Suspense fallback={null}>
        <SearchParamsHandler onRedirectChange={setRedirectUrl} />
      </Suspense>
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
            Welcome back
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Sign in to continue to your Nexus workspace
          </p>
        </div>

        {/* Form content */}
        <div className="space-y-6">
          {/* OAuth buttons first - matching reference */}
          <OAuthButtons redirectUrl={redirectUrl} />

          <FieldSeparator>Or</FieldSeparator>

          {/* Email/password form */}
          {showMagicLink ? (
            <MagicLinkForm
              setShowMagicLink={setShowMagicLink}
              redirectUrl={redirectUrl}
            />
          ) : (
            <LoginForm
              setShowMagicLink={setShowMagicLink}
              redirectUrl={redirectUrl}
            />
          )}

          {/* Sign up link */}
          <p className="text-center text-sm text-zinc-500 dark:text-zinc-400">
            Don&apos;t have an account?{" "}
            <Link
              href="/signup"
              className="font-medium text-primary transition-colors hover:text-primary/80"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
